import { ChangeEvent, FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import Datepicker, { DateValueType } from "react-tailwindcss-datepicker";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { InputField } from "@/components/InputField/InputField";
import {
	DropdownWithCheckboxes,
	DropdownWithCheckboxesItem,
} from "@/components/DropdownWithCheckboxes/DropdownWithCheckboxes";

import {
	COMPANY_SIZE_BUCKETS,
	ConnectionsFilterOptions,
	ConnectionsFilters,
	SCORE_VALUES,
} from "../schema/connections-filters";

interface LinkedinConnectionsFilterPanelProps {
	filters: ConnectionsFilters;
	options?: ConnectionsFilterOptions;
	showEnrichmentFilter: boolean;
	onChange: (filters: ConnectionsFilters) => void;
	onReset: () => void;
}

const toItems = (values: string[]): DropdownWithCheckboxesItem[] =>
	values.map((value) => ({ id: value, title: value }));

const selectedItems = (values: string[]): DropdownWithCheckboxesItem[] => toItems(values);

/** A picker value as the plain `YYYY-MM-DD` the API filters on; empty when unset. */
const toIsoDay = (value: Date | string | null | undefined): string =>
	value ? new Date(value).toISOString().slice(0, 10) : "";

/**
 * The filter panel behind the toolbar's Filter button (PREC-3914).
 *
 * Every control here filters in the API across the whole network, not the page on screen. The
 * dropdown values come from the user's own connections (`options`), so the panel cannot offer a
 * country or industry that would filter the table down to nothing.
 */
export const LinkedinConnectionsFilterPanel: FunctionComponent<
	LinkedinConnectionsFilterPanelProps
> = ({ filters, options, showEnrichmentFilter, onChange, onReset }) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`account.myConnections.${key}`);

	const setList = (key: keyof ConnectionsFilters) => (items: DropdownWithCheckboxesItem[]) =>
		onChange({ ...filters, [key]: items.map((item) => String(item.id)) });

	const setText =
		(key: keyof ConnectionsFilters) =>
		(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
			onChange({ ...filters, [key]: event.target.value });

	const connectedRange: DateValueType = {
		startDate: filters.connectedFrom ? new Date(filters.connectedFrom) : null,
		endDate: filters.connectedTo ? new Date(filters.connectedTo) : null,
	};

	/** The picker hands back Date objects; the API takes plain ISO days. */
	const setConnectedRange = (value: DateValueType) =>
		onChange({
			...filters,
			connectedFrom: toIsoDay(value?.startDate),
			connectedTo: toIsoDay(value?.endDate),
		});

	const enrichmentItems: DropdownWithCheckboxesItem[] = [
		{ id: "ENRICHED", title: ts("showEnriched") },
		{ id: "NON_ENRICHED", title: ts("showNonEnriched") },
	];

	return (
		<div
			className="grid grid-cols-1 gap-3 border-b border-border-light bg-white px-2 py-3 modal-m:grid-cols-3 desktop:grid-cols-4"
			data-testid="connections-filter-panel"
		>
			<DropdownWithCheckboxes
				allowEmpty
				portal
				data={toItems(SCORE_VALUES)}
				label={ts("filterScore")}
				size={ButtonSize.XS}
				title={ts("filterAny")}
				value={selectedItems(filters.scores)}
				handleSelect={setList("scores")}
			/>

			{showEnrichmentFilter && (
				<DropdownWithCheckboxes
					allowEmpty
					portal
					data={enrichmentItems}
					label={ts("filterEnrichment")}
					size={ButtonSize.XS}
					title={ts("filterAny")}
					value={
						filters.enrichmentFilter === "ALL"
							? []
							: enrichmentItems.filter((item) => item.id === filters.enrichmentFilter)
					}
					handleSelect={(items) =>
						onChange({
							...filters,
							// One of the two, or neither — "enriched AND not enriched" is every row.
							enrichmentFilter:
								(items[items.length - 1]?.id as ConnectionsFilters["enrichmentFilter"]) ?? "ALL",
						})
					}
				/>
			)}

			<InputField
				classNameWrapper="!mb-0"
				label={ts("filterCompanyName")}
				name="filter-company-name"
				placeholder={ts("filterCompanyNamePlaceholder")}
				size={ButtonSize.XS}
				value={filters.companyName}
				handleChange={setText("companyName")}
			/>

			<InputField
				classNameWrapper="!mb-0"
				label={ts("filterJobTitle")}
				name="filter-job-title"
				placeholder={ts("filterJobTitlePlaceholder")}
				size={ButtonSize.XS}
				value={filters.jobTitle}
				handleChange={setText("jobTitle")}
			/>

			<DropdownWithCheckboxes
				allowEmpty
				portal
				data={toItems(options?.countries ?? [])}
				label={ts("filterCountry")}
				size={ButtonSize.XS}
				title={ts("filterAny")}
				value={selectedItems(filters.countries)}
				handleSelect={setList("countries")}
			/>

			<DropdownWithCheckboxes
				allowEmpty
				portal
				data={toItems(options?.seniorities ?? [])}
				label={ts("filterSeniority")}
				size={ButtonSize.XS}
				title={ts("filterAny")}
				value={selectedItems(filters.seniorities)}
				handleSelect={setList("seniorities")}
			/>

			<DropdownWithCheckboxes
				allowEmpty
				portal
				data={toItems(options?.languages ?? [])}
				label={ts("filterLanguage")}
				size={ButtonSize.XS}
				title={ts("filterAny")}
				value={selectedItems(filters.languages)}
				handleSelect={setList("languages")}
			/>

			<DropdownWithCheckboxes
				allowEmpty
				portal
				data={toItems(options?.companyIndustries ?? [])}
				label={ts("filterCompanyIndustry")}
				size={ButtonSize.XS}
				title={ts("filterAny")}
				value={selectedItems(filters.companyIndustries)}
				handleSelect={setList("companyIndustries")}
			/>

			<DropdownWithCheckboxes
				allowEmpty
				portal
				data={toItems(options?.companyCountries ?? [])}
				label={ts("filterCompanyCountry")}
				size={ButtonSize.XS}
				title={ts("filterAny")}
				value={selectedItems(filters.companyCountries)}
				handleSelect={setList("companyCountries")}
			/>

			<DropdownWithCheckboxes
				allowEmpty
				portal
				data={COMPANY_SIZE_BUCKETS.map((bucket) => ({ id: bucket.id, title: bucket.title }))}
				label={ts("filterCompanySize")}
				size={ButtonSize.XS}
				title={ts("filterAny")}
				value={selectedItems(filters.companySizeBuckets)}
				handleSelect={setList("companySizeBuckets")}
			/>

			<div>
				<span className="label-styles">{ts("filterConnectedOn")}</span>
				<Datepicker
					useRange
					containerClassName="relative"
					inputClassName="input-styles h-[32px] w-full rounded-[10px] px-3 text-xs"
					placeholder={ts("filterConnectedOnPlaceholder")}
					separator="-"
					value={connectedRange}
					onChange={setConnectedRange}
				/>
			</div>

			<div className="flex items-end">
				<Button
					color={ButtonColor.ACTION_SECONDARY}
					size={ButtonSize.S}
					testId="reset-connection-filters"
					title={ts("filterReset")}
					onClick={onReset}
				/>
			</div>
		</div>
	);
};
